/**
 * How this viewer likes their radar drawn.
 *
 * Device-local on purpose, like the home point in `home.ts`: this is a display
 * preference, not tenant data. Promoting it to the user record would mean a schema
 * change, an endpoint and a migration, and the tenant-wide audited settings behind
 * `/api/settings/*` are far too heavy a mechanism for "how blurry do I like my radar".
 *
 * The numbers live here rather than in WeatherMap so there is one place to tune them.
 */

export type RadarSharpness = 'soft' | 'normal' | 'sharp';

export interface RadarPrefs {
  sharpness: RadarSharpness;
  /** 0..1, the existing opacity slider — persisted here so it survives a remount. */
  opacity: number;
  /** null means "follow whatever the server is configured to serve". */
  provider: string | null;
  /** 'reflectivity' | 'precip-type'; null follows the server default. */
  product: string | null;
  /** How far ahead the storm-path swath projects, in minutes. */
  swathMinutes: number;
}

export const RADAR_PREFS_DEFAULT: RadarPrefs = {
  sharpness: 'normal',
  opacity: 0.65,
  provider: null,
  product: null,
  swathMinutes: 60,
};

/** Offered lead times. A projection much beyond an hour and a half is storytelling. */
export const SWATH_MINUTES: number[] = [30, 60, 90];

/**
 * The lever that actually softens the picture.
 *
 * NOAA's grid is 1.1 km, which at latitude 35 is about 1.1px at z7, 4.4px at z9 and
 * 17.6px at z11 — those are the blocks on screen. With no `maxzoom` MapLibre asks for
 * a fresh native render at every zoom, so nothing is ever upscaled and its linear
 * filtering has nothing to smooth. Capping `maxzoom` makes it GPU-upscale a lower-zoom
 * tile instead, which blends the cell edges into gradients.
 *
 * z7 is native resolution, so Soft discards no real data; going lower would. Sharp
 * still caps at 10 because above that every extra render is provably zero-information —
 * it is the same 1.1 km cells drawn bigger, at the cost of a fresh upstream request.
 */
export const RADAR_MAX_ZOOM: Record<RadarSharpness, number> = {
  soft: 7,
  normal: 8,
  sharp: 10,
};

export interface RadarPaint {
  'raster-resampling': 'linear' | 'nearest';
  'raster-saturation': number;
  'raster-contrast': number;
}

/**
 * Categorical products get their own treatment. Linear filtering across a rain/snow
 * boundary produces an intermediate colour belonging to NO category — an invented
 * "half snow" band along every edge. Honest on a continuous reflectivity ramp, actively
 * misleading on a seven-colour classification. Nearest-neighbour gives honest blocks.
 */
export const RADAR_PAINT_CATEGORICAL: RadarPaint = {
  'raster-resampling': 'nearest',
  'raster-saturation': 0,
  'raster-contrast': 0,
};

/** `raster-opacity` is not here: it stays an independent user control. */
export const RADAR_PAINT: Record<RadarSharpness, RadarPaint> = {
  soft: { 'raster-resampling': 'linear', 'raster-saturation': -0.2, 'raster-contrast': -0.1 },
  normal: { 'raster-resampling': 'linear', 'raster-saturation': -0.1, 'raster-contrast': 0 },
  // 'nearest' is what makes Sharp read as crisp rather than merely less blurry.
  sharp: { 'raster-resampling': 'nearest', 'raster-saturation': 0, 'raster-contrast': 0.1 },
};

/** Default opacity per mode, applied only when the viewer has not set their own. */
export const RADAR_OPACITY: Record<RadarSharpness, number> = { soft: 0.5, normal: 0.65, sharp: 0.8 };

export const SHARPNESS_LABELS: Array<[RadarSharpness, string]> = [
  ['soft', 'Soft'],
  ['normal', 'Normal'],
  ['sharp', 'Sharp'],
];

const KEY = (tenantId: string) => `ww.radar.${tenantId}`;
const SHARPNESS = new Set<string>(['soft', 'normal', 'sharp']);

function clean(raw: unknown): RadarPrefs {
  const c = raw as Partial<RadarPrefs> | null;
  if (!c || typeof c !== 'object') return { ...RADAR_PREFS_DEFAULT };
  const sharpness = typeof c.sharpness === 'string' && SHARPNESS.has(c.sharpness)
    ? (c.sharpness as RadarSharpness)
    : RADAR_PREFS_DEFAULT.sharpness;
  const opacity = typeof c.opacity === 'number' && Number.isFinite(c.opacity)
    ? Math.min(1, Math.max(0, c.opacity))
    : RADAR_OPACITY[sharpness];
  // An unknown provider is dropped here and would be rejected by the server's
  // allow-list anyway; belt and braces, because it ends up in a URL.
  const id = (v: unknown) => (typeof v === 'string' && /^[a-z0-9_-]{1,32}$/.test(v) ? v : null);
  const provider = id(c.provider);
  const product = id(c.product);
  // Only the offered values; an arbitrary number here would project an arbitrary claim.
  const swathMinutes = SWATH_MINUTES.includes(c.swathMinutes as number)
    ? (c.swathMinutes as number)
    : RADAR_PREFS_DEFAULT.swathMinutes;
  return { sharpness, opacity, provider, product, swathMinutes };
}

/** Always returns usable prefs. A null tenant id is normal on the public alert page. */
export function getRadarPrefs(tenantId: string | null | undefined): RadarPrefs {
  if (!tenantId) return { ...RADAR_PREFS_DEFAULT };
  try {
    const raw = window.localStorage.getItem(KEY(tenantId));
    return raw ? clean(JSON.parse(raw) as unknown) : { ...RADAR_PREFS_DEFAULT };
  } catch {
    // Private browsing, blocked storage, corrupt JSON — all the same to us.
    return { ...RADAR_PREFS_DEFAULT };
  }
}

export function setRadarPrefs(tenantId: string | null | undefined, patch: Partial<RadarPrefs>): RadarPrefs {
  const next = clean({ ...getRadarPrefs(tenantId), ...patch });
  if (!tenantId) return next;
  try {
    window.localStorage.setItem(KEY(tenantId), JSON.stringify(next));
  } catch {
    /* storage unavailable; the choice still applies for this session */
  }
  return next;
}
